import { createRouter, createWebHistory } from "vue-router";
import { ROUTE_NAMES } from "../../app-constants";
import Menu from "./menu/Menu.vue";
import ShoppingCart from "./shopping-cart/ShoppingCart";
import Checkout from "./checkout/Checkout";
import Login from "./login/Login";
import ContinueAsGuest from "./continue-as-guest/ContinueAsGuest";
import Registration from "./registration/Registration";
import UserProfile from "./user-profile/UserProfile";
import routeGuards from "./routeGuards";
import AppContainer from "./app-container/AppContainer";
import NotFound from "./not-found/NotFoundPage";
import OrderConfirmation from "./order-confirmation/OrderConfirmation";
import ChangePassword from "./user-profile/components/change-password/ChangePassword";
import ForgotPassword from "./user-profile/components/forgot-password/ForgotPassword";
import ResetPassword from "./user-profile/components/reset-password/ResetPassword";
import UserPersonalInformation from "./user-profile/components/user-personal-information/UserPersonalInformation";
import UserBillingInformation from "./user-profile/components/user-billing-information/UserBillingInformation";
import UserWallet from "./user-profile/components/user-wallet/UserWallet";
import Help from "./help/Help";
import VenueContainer from "./venue-container/VenueContainer";
import VendorPicker from "../components/venue/vendor-picker/VendorPicker";
import Offerings from "./offerings/Offerings";
import Favorites from "./favorites/Favorites";
import OrderAgain from "./order-again/OrderAgain";
import StyleGuide from "./style-guide/StyleGuide";

export const DEFAULT_TITLE = "Appetize";

export const routes = [
  {
    path: "/not-found",
    name: ROUTE_NAMES.NOT_FOUND,
    component: NotFound,
    meta: {
      title: "Not found"
    }
  },
  {
    path: "/reset-password",
    name: ROUTE_NAMES.RESET_PASSWORD,
    component: ResetPassword,
    beforeEnter: routeGuards.resetPasswordPageGuard,
    meta: {
      title: "Reset password"
    }
  },
  {
    path: "/",
    redirect: "/not-found"
  },
  {
    path: "/style-guide",
    component: StyleGuide,
    meta: {
      title: "Style Guide"
    }
  },
  {
    path: "/:venue",
    component: AppContainer,
    children: [
      {
        path: "",
        props: true,
        name: ROUTE_NAMES.VENUE_CONTAINER,
        component: VenueContainer,
        beforeEnter: [
          routeGuards.removeDeepLinkedFlag,
          routeGuards.saveURLTenderAndRemoveQuerys,
          routeGuards.appPageGuard,
          routeGuards.venuePageGuard
        ],
        meta: {
          title: "Venue details"
        }
      },
      {
        path: "level/:level/section/:section",
        props: true,
        component: VendorPicker,
        meta: {
          title: "Vendors"
        },
        children: [
          {
            path: "",
            name: ROUTE_NAMES.DEEP_LINK_SECTION,
            beforeEnter: [routeGuards.saveURLTenderAndRemoveQuerys, routeGuards.appPageGuard, routeGuards.deepLinkGuard]
          },
          {
            path: "row/:row",
            name: ROUTE_NAMES.DEEP_LINK_ROW,
            beforeEnter: [routeGuards.saveURLTenderAndRemoveQuerys, routeGuards.appPageGuard, routeGuards.deepLinkGuard]
          },
          {
            path: "row/:row/seat/:seat",
            name: ROUTE_NAMES.DEEP_LINK_SEAT,
            beforeEnter: [routeGuards.saveURLTenderAndRemoveQuerys, routeGuards.appPageGuard, routeGuards.deepLinkGuard]
          }
        ]
      },
      {
        path: "vendors",
        props: true,
        name: ROUTE_NAMES.VENDOR_PICKER,
        component: VendorPicker,
        beforeEnter: [routeGuards.saveURLTenderAndRemoveQuerys, routeGuards.appPageGuard, routeGuards.vendorsPageGuard],
        meta: {
          title: "Vendor picker"
        }
      },
      {
        path: "vendors/:vendor",
        name: ROUTE_NAMES.VENDOR,
        redirect: {
          name: ROUTE_NAMES.OFFERINGS
        }
      },
      {
        path: "vendors/:vendor/offerings",
        name: ROUTE_NAMES.OFFERINGS,
        component: Offerings,
        beforeEnter: [
          routeGuards.saveURLTenderAndRemoveQuerys,
          routeGuards.vendorMiddlewareGuard,
          routeGuards.offeringsPageGuard
        ],
        meta: {
          title: "Offerings"
        }
      },
      {
        path: "vendors/:vendor?/favorites",
        name: ROUTE_NAMES.FAVORITES,
        component: Favorites,
        beforeEnter: [routeGuards.vendorMiddlewareGuard, routeGuards.favoritesPageGuard],
        meta: {
          title: "Favorites"
        }
      },
      {
        path: "vendors/:vendor?/order-again",
        name: ROUTE_NAMES.ORDER_AGAIN,
        component: OrderAgain,
        beforeEnter: [routeGuards.vendorMiddlewareGuard, routeGuards.orderAgainPageGuard],
        meta: {
          title: "User Orders"
        }
      },
      {
        path: "vendors/:vendor/menu",
        props: true,
        name: ROUTE_NAMES.MENU,
        component: Menu,
        beforeEnter: [routeGuards.saveURLTenderAndRemoveQuerys, routeGuards.vendorMiddlewareGuard, routeGuards.menuPageGuard],
        meta: {
          title: (to) => `${to.meta?.pageTitle} menu offerings`
        }
      },
      {
        path: "cart",
        name: ROUTE_NAMES.SHOPPING_CART,
        component: ShoppingCart,
        beforeEnter: [routeGuards.appPageGuard, routeGuards.shoppingCartPageGuard],
        meta: {
          title: "Shopping cart"
        }
      },
      {
        path: "checkout",
        name: ROUTE_NAMES.CHECKOUT,
        component: Checkout,
        beforeEnter: [
          routeGuards.saveURLTenderAndRemoveQuerys,
          routeGuards.validateCartBeforeCheckout,
          routeGuards.appPageGuard,
          routeGuards.checkoutPageGuard
        ],
        meta: {
          title: "Checkout"
        }
      },
      {
        path: "login",
        name: ROUTE_NAMES.LOGIN,
        component: Login,
        beforeEnter: [routeGuards.appPageGuard, routeGuards.loginPageGuard],
        props: true,
        meta: {
          title: "Login"
        }
      },
      {
        path: "signup",
        name: ROUTE_NAMES.REGISTRATION,
        component: Registration,
        beforeEnter: [routeGuards.appPageGuard, routeGuards.loginPageGuard],
        meta: {
          title: "Registration"
        }
      },
      {
        path: "forgot-password",
        name: ROUTE_NAMES.FORGOT_PASSWORD,
        component: ForgotPassword,
        beforeEnter: [routeGuards.appPageGuard, routeGuards.loginPageGuard],
        meta: {
          title: "Forgot password"
        }
      },
      {
        path: "continue-as-guest",
        name: ROUTE_NAMES.CONTINUE_AS_GUEST,
        component: ContinueAsGuest,
        meta: {
          title: "Continue as Guest"
        }
      },
      {
        path: "profile",
        name: ROUTE_NAMES.USER_PROFILE,
        component: UserProfile,
        beforeEnter: [routeGuards.appPageGuard, routeGuards.userRouteGuard],
        meta: {
          title: "User profile"
        }
      },
      {
        path: "confirmation",
        name: ROUTE_NAMES.ORDER_CONFIRMATION,
        component: OrderConfirmation,
        beforeEnter: [routeGuards.orderConfirmationRouteGuard],
        meta: {
          title: "Order confirmation"
        }
      },
      {
        path: "profile/order/:id",
        name: ROUTE_NAMES.ORDER_DETAILS,
        component: OrderConfirmation,
        beforeEnter: [routeGuards.orderDetailsGuard],
        meta: {
          title: "Order details"
        }
      },
      {
        path: "profile/change-password",
        name: ROUTE_NAMES.CHANGE_PASSWORD,
        component: ChangePassword,
        beforeEnter: [routeGuards.appPageGuard, routeGuards.userRouteGuard],
        meta: {
          title: "Change password"
        }
      },
      {
        path: "profile/personal-information",
        name: ROUTE_NAMES.USER_INFO,
        component: UserPersonalInformation,
        beforeEnter: [routeGuards.appPageGuard, routeGuards.userRouteGuard],
        meta: {
          title: "Personal information"
        }
      },
      {
        path: "profile/billing-information",
        name: ROUTE_NAMES.BILLING_INFO,
        component: UserBillingInformation,
        beforeEnter: [routeGuards.appPageGuard, routeGuards.userRouteGuard],
        meta: {
          title: "Billing information"
        }
      },
      {
        path: "profile/wallet",
        name: ROUTE_NAMES.WALLET,
        component: UserWallet,
        beforeEnter: [routeGuards.appPageGuard, routeGuards.userRouteGuard],
        meta: {
          title: "Wallet"
        }
      },
      {
        path: "help",
        name: ROUTE_NAMES.HELP,
        component: Help,
        meta: {
          title: "Help"
        }
      },
      {
        path: "*",
        redirect: "/not-found"
      }
    ]
  }
];

const router = createRouter({
  history: createWebHistory("/"),
  routes
});

export function afterEach(to, from) {
  if (to.name !== from?.name) {
    document.title = typeof to.meta.title === "function" ? to.meta.title(to, from) : to.meta.title || DEFAULT_TITLE;
  }
}

router.afterEach(afterEach.bind(null));

export default router;
